import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { FloatingList } from './FloatingList';
import { useServerFieldError } from './fieldErrors';
import { Chip } from './Chip';
import { FieldMessage, fieldDescription } from './TextField';
import type { SelectOption } from './Select';
import { uiTexts } from './texts';

export interface MultiSelectProps {
  /** The options to pick from; with `search` only the labels of known values (optional). */
  options?: SelectOption[];
  /**
   * Server search for lists too large to load (parties, locations): at most a page of matches for the typed text
   * (empty text: the first page). Debounced; an answer to an older text is dropped.
   */
  search?: (text: string) => Promise<SelectOption[]>;
  /** Labels of picked values the picker has not seen in a result (a URL filter, a record being edited). */
  resolve?: (values: string[]) => Promise<SelectOption[]>;
  /** Characters before the first search (default 0: the first page shows on focus). */
  minChars?: number;
  value: string[];
  onChange: (value: string[]) => void;
  /** Visible label; without it give `aria-label`. */
  label?: ReactNode;
  'aria-label'?: string;
  placeholder?: string;
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  /** At least one value (validity of the input, `aria-required`). */
  required?: boolean;
  /** The field's name: inside a `FormDialog` it gets the server's message for it. */
  name?: string;
  /** Typed values that are no option can be added (Enter, comma or a pasted list). */
  creatable?: boolean;
  /** At most this many values; the other options are disabled once reached. */
  max?: number;
  id?: string;
  /** Class of the wrapping `.nyk-ui-field` (grid placement); `className` goes to the chip box. */
  fieldClassName?: string;
  className?: string;
  'data-testid'?: string;
  /** Texts for other languages. */
  texts?: { noMatch?: string; add?: (v: string) => string; remove?: (label: string) => string };
}

const norm = (s: string) => s.toLocaleLowerCase();
const split = (s: string) =>
  s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Several values from a list: the picks as removable chips in front of a combobox that filters the options by label,
 * value or group as you type. Arrows move, Enter or a click toggles (the list stays open), Backspace on an empty input
 * removes the last pick, Escape closes. `creatable` also takes typed values. `value` keeps the order of picking.
 * With `search` the options come from the server as you type (`resolve` names picks it has not returned).
 */
export function MultiSelect({
  options: given = [],
  search,
  resolve,
  minChars = 0,
  value,
  onChange,
  label,
  placeholder,
  hint,
  error: ownError,
  disabled,
  required,
  name,
  creatable,
  max,
  id,
  fieldClassName,
  className,
  texts,
  ...rest
}: MultiSelectProps) {
  const auto = useId();
  const inputId = id ?? auto;
  const listId = useId();
  const server = useServerFieldError(name);
  const error = ownError ?? server;
  const ids = fieldDescription(inputId, hint, error);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    input.current?.setCustomValidity(required && !value.length ? uiTexts.required() : '');
  }, [required, value.length]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const full = max != null && value.length >= max;

  // Async mode: the options are the last search result; labels of picks come from what was seen or resolved.
  const [found, setFound] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [settled, setSettled] = useState('');
  const seen = useRef(new Map<string, SelectOption>());
  const [, setSeenVersion] = useState(0);
  const searchRef = useRef(search);
  searchRef.current = search;
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  const seq = useRef(0);
  useEffect(() => {
    if (!search) return;
    const t = setTimeout(() => setSettled(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query, search]);
  useEffect(() => {
    if (!searchRef.current || !open || settled.length < minChars) return;
    const n = ++seq.current;
    setLoading(true);
    searchRef
      .current(settled)
      .then((list) => {
        if (n !== seq.current) return;
        for (const o of list) seen.current.set(o.value, o);
        setFound(list);
      })
      .catch(() => n === seq.current && setFound([]))
      .finally(() => n === seq.current && setLoading(false));
  }, [settled, open, minChars]);
  const unknown = search
    ? value.filter((v) => !seen.current.has(v) && !given.some((o) => o.value === v))
    : [];
  const unknownKey = unknown.join('\u0000');
  useEffect(() => {
    if (!unknownKey || !resolveRef.current) return;
    let live = true;
    resolveRef
      .current(unknownKey.split('\u0000'))
      .then((list) => {
        if (!live) return;
        for (const o of list) seen.current.set(o.value, o);
        setSeenVersion((x) => x + 1);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [unknownKey]);
  const options = search ? found : given;
  const labelOf = (v: string) => given.find((o) => o.value === v)?.label ?? seen.current.get(v)?.label ?? v;
  const ariaLabel = typeof label === 'string' ? label : rest['aria-label'];

  const q = norm(query).trim();
  // a server search has matched already
  const shown = useMemo(
    () =>
      q && !search
        ? options.filter((o) => [o.label, o.value, o.group ?? ''].some((t) => norm(t).includes(q)))
        : options,
    [options, q, search]
  );
  // "Add …" first when the typed text is no option and not picked yet
  const typed = query.trim();
  const canCreate =
    !!creatable &&
    !!typed &&
    !full &&
    !value.includes(typed) &&
    !options.some((o) => norm(o.value) === q || norm(o.label) === q);
  const rows: { option?: SelectOption; create?: string }[] = [
    ...(canCreate ? [{ create: typed }] : []),
    ...shown.map((option) => ({ option })),
  ];
  const isOn = (o: SelectOption) => value.includes(o.value);
  const pickable = (i: number) => {
    const r = rows[i];
    if (!r) return false;
    if (r.create) return true;
    return !r.option!.disabled && (isOn(r.option!) || !full);
  };

  const toggle = (v: string) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : full ? value : [...value, v]);
  const choose = (i: number) => {
    const r = rows[i];
    if (!r || !pickable(i)) return;
    if (r.create) {
      // a pasted list: known options by label or value, each value once
      const known = (t: string) =>
        options.find((o) => !o.disabled && (norm(o.value) === norm(t) || norm(o.label) === norm(t)))?.value;
      const add = [...new Set(split(r.create).map((t) => known(t) ?? t))].filter((x) => !value.includes(x));
      onChange([...value, ...add].slice(0, max ?? Infinity));
    } else toggle(r.option!.value);
    setQuery('');
    setActive(-1);
  };
  const move = (step: 1 | -1) => {
    if (!rows.length) return;
    let i = active;
    for (let n = 0; n < rows.length; n++) {
      i = (i + step + rows.length) % rows.length;
      if (pickable(i)) break;
    }
    setActive(i);
    document.getElementById(`${listId}-${i}`)?.scrollIntoView?.({ block: 'nearest' });
  };
  const keys = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      move(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'Enter') {
      if (!open && !canCreate) return;
      e.preventDefault();
      if (pickable(active)) choose(active);
      else if (canCreate) choose(0);
      else if (q) {
        const i = rows.findIndex((_, n) => pickable(n));
        if (i >= 0) choose(i);
      }
    } else if (e.key === ',' && creatable && typed) {
      e.preventDefault();
      if (canCreate) choose(0);
    } else if (e.key === 'Backspace' && !query && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setActive(-1);
    }
  };

  let lastGroup: string | undefined;
  const control = (
    <div
      ref={box}
      className={['nyk-ui-combo', 'nyk-ui-multi', className].filter(Boolean).join(' ')}
      data-open={open ? '' : undefined}
      data-testid={rest['data-testid']}
    >
      <div
        className="nyk-ui-multi-box"
        data-disabled={disabled ? '' : undefined}
        onMouseDown={(e) => {
          // a click on the box (not a chip's button) focuses the input
          if (e.target === e.currentTarget) {
            e.preventDefault();
            input.current?.focus();
          }
        }}
      >
        {value.map((v) => (
          <Chip
            key={v}
            size="sm"
            data-value={v}
            disabled={disabled}
            removeLabel={texts?.remove?.(labelOf(v)) ?? uiTexts.remove(labelOf(v))}
            onRemove={() => {
              onChange(value.filter((x) => x !== v));
              input.current?.focus();
            }}
          >
            {labelOf(v)}
          </Chip>
        ))}
        <input
          ref={input}
          id={inputId}
          className="nyk-ui-multi-input"
          role="combobox"
          aria-label={label ? undefined : ariaLabel}
          aria-describedby={ids.describedBy}
          aria-invalid={error ? true : undefined}
          aria-required={required || undefined}
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && pickable(active) ? `${listId}-${active}` : undefined}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          placeholder={value.length ? undefined : placeholder}
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onBlur={() => {
            if (canCreate) choose(0);
            setOpen(false);
            setActive(-1);
            setQuery('');
          }}
          onKeyDown={keys}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
        />
      </div>
      {open && !disabled && (
        <FloatingList
          anchor={box}
          id={listId}
          role="listbox"
          aria-busy={loading || undefined}
          aria-multiselectable="true"
          aria-label={ariaLabel}
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading && !rows.length && <div className="nyk-ui-combo-empty">{uiTexts.loading()}</div>}
          {!loading && !rows.length && (!search || settled.length >= minChars) && (
            <div className="nyk-ui-combo-empty">{texts?.noMatch ?? uiTexts.noMatch()}</div>
          )}
          {rows.map((r, i) => {
            if (r.create)
              return (
                <div
                  key="\u0000create"
                  id={`${listId}-${i}`}
                  role="option"
                  className="nyk-ui-combo-opt nyk-ui-combo-create"
                  aria-selected={false}
                  data-active={i === active ? '' : undefined}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(i)}
                >
                  {texts?.add?.(r.create) ?? uiTexts.add(r.create)}
                </div>
              );
            const o = r.option!;
            const heading = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            const off = !pickable(i);
            return (
              <div key={o.value}>
                {heading && <div className="nyk-ui-combo-group">{heading}</div>}
                <div
                  id={`${listId}-${i}`}
                  role="option"
                  className="nyk-ui-combo-opt"
                  aria-selected={isOn(o)}
                  aria-disabled={off || undefined}
                  data-active={i === active ? '' : undefined}
                  onMouseEnter={() => !off && setActive(i)}
                  onClick={() => choose(i)}
                >
                  <span className="nyk-ui-multi-check" aria-hidden="true">
                    {isOn(o) ? '✓' : ''}
                  </span>
                  {o.label}
                </div>
              </div>
            );
          })}
        </FloatingList>
      )}
    </div>
  );
  return label ? (
    <div className={['nyk-ui-field', fieldClassName].filter(Boolean).join(' ')}>
      <label htmlFor={inputId}>{label}</label>
      {control}
      <FieldMessage hint={hint} error={error} ids={ids} />
    </div>
  ) : (
    <>
      {control}
      <FieldMessage hint={hint} error={error} ids={ids} />
    </>
  );
}
